import { useState, useCallback, useMemo, useEffect } from "react";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { Button } from "@/components/ui/button";
import { OrderFormField } from "./order-form-field";
import { OrderFormInput } from "./order-form-input";
import { useMarket } from "@/solana/hooks/use-markets";
import { useAccountSummary } from "@/solana/hooks/use-account-summary";
import { useProduct } from "@/features/product/product-context";
import { TradingEngine } from "@/solana/trading-engine";
import { type MarketSymbol } from "@/solana/constants";
import { formatCurrency } from "@/lib/utils";
import { toast } from "sonner";
import { useTradingSettings } from "@/features/trading-settings/trading-settings-context";
import { fetchLeverageConfig, getMaxPositionFromConfig, type LeverageConfig } from "@/features/trading-settings/types";
import { AccountModeDialog } from "@/features/trading-settings/account-mode-dialog";
import { LeverageDialog } from "@/features/trading-settings/leverage-dialog";
import { SlippageToleranceDialog } from "@/features/trading-settings/slippage-tolerance-dialog";
import { ValuePercentageSlider } from "./value-percentage-slider";
import { useMediaQuery } from "@/hooks/use-media-query";
import { ArrowLeftRight } from "lucide-react";
import { LoginModal } from "@/components/auth/login-modal";
import { useAccountStore, useRealTimeUnrealizedPnL, useMarketPrice } from "@/backend/store";
import { useFeesConfig } from "@/backend/hooks/use-fees-config";
import { useVlpQuote } from "@/backend/hooks/use-vlp-quotes";
import { useSlippageEstimation } from "./use-slippage-estimation";
import { SlippageWarning } from "./slippage-warning";
import { LiquidationPriceDisplay } from "./liquidation-price-display";
import { usePositions } from "@/solana/hooks/use-positions";
import { useOrderPreview } from "@/backend/hooks/use-order-preview";

type Asset = {
  code: string;
  precision: number;
};

type OrderFormProps = {
  base: Asset;
  quote: Asset;
  market: MarketSymbol;
  panelId: string;
};

const invalidChars = ["e", "E", "+", "-"];

// Industry standard: 0.5% tolerance for min order validation to handle floating-point precision
const MIN_ORDER_TOLERANCE = 0.995;

export const OrderForm = ({ base, quote, market, panelId: _panelId }: OrderFormProps) => {
  const { userId, isAuthenticated } = useUnifiedAuth();
  const { market: _marketData } = useMarket(market);
  const { summary } = useAccountSummary();
  const { product } = useProduct();
  
  // Get minimum order value from product (default $10)
  const minOrderValueUsd = product?.minOrderValueUsd || 10;
  const { accountMode, getLeverage, setLeverage, slippageTolerance, setSlippageTolerance } = useTradingSettings();
  const { data: feesConfig } = useFeesConfig();
  
  // VLP quote for slippage calculation (use rawQuote for string format)
  const { rawQuote: vlpQuote } = useVlpQuote(market);
  
  // Check for existing position in this market
  const positionsMap = useAccountStore((state) => state.positions);
  const existingPosition = positionsMap.get(market);
  const positionLeverage = existingPosition && existingPosition.size > 0 
    ? existingPosition.leverage 
    : null;
  
  // Get leverage for the current market
  // Always use stored leverage (user's setting) as the source of truth
  // The position leverage will update via WebSocket after leverage changes
  const storedLeverage = getLeverage(market);
  const leverage = storedLeverage;
  
  // Sync stored leverage with position leverage when position updates from WebSocket
  // This ensures the setting matches the actual position leverage
  useEffect(() => {
    if (positionLeverage !== null && positionLeverage !== storedLeverage) {
      setLeverage(market, positionLeverage);
    }
  }, [positionLeverage, storedLeverage, market, setLeverage]);
  
  // Determine effective user ID and connection status (works for both wallet and email users)
  const effectiveUserId = userId;
  const isEffectivelyConnected = isAuthenticated;

  const [orderValue, setOrderValue] = useState("");
  const [valueCurrency, setValueCurrency] = useState<"quote" | "base">("quote"); // USDC or BTC/SOL/ETH
  const [orderType, setOrderType] = useState<"market" | "limit">("market");
  const [limitPrice, setLimitPrice] = useState("");
  const [timeInForce, setTimeInForce] = useState<"GTC" | "IOC" | "ALO">("GTC");
  const [reduceOnly, setReduceOnly] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Take Profit / Stop Loss state (for limit orders)
  const [takeProfit, setTakeProfit] = useState<string | null>(null);
  const [stopLoss, setStopLoss] = useState<string | null>(null);
  
  // Focus states for inputs (to control formatting)
  const [isValueFocused, setIsValueFocused] = useState(false);
  const [isLimitPriceFocused, setIsLimitPriceFocused] = useState(false);
  
  // Dialog states
  const [accountModeDialogOpen, setAccountModeDialogOpen] = useState(false);
  const [leverageDialogOpen, setLeverageDialogOpen] = useState(false);
  const [slippageDialogOpen, setSlippageDialogOpen] = useState(false);
  const [orderTypeMenuOpen, setOrderTypeMenuOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 1000px)");
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  
  // Handler to check auth before performing actions
  const requireAuth = useCallback((action: () => void) => {
    if (!isEffectivelyConnected) {
      setLoginModalOpen(true);
      return;
    }
    action();
  }, [isEffectivelyConnected]);
  
  // Fetch leverage config from API
  const [leverageConfig, setLeverageConfig] = useState<LeverageConfig | null>(null);
  useEffect(() => {
    fetchLeverageConfig(market).then(setLeverageConfig);
  }, [market]);

  // Subscribe to real-time unrealized PnL (calculated in store)
  const realTimeUnrealizedPnL = useRealTimeUnrealizedPnL();

  // Get positions for real-time used margin calculation (still needed)
  const { positions } = usePositions();
  
  // Calculate real-time used margin from open positions
  // Each position's margin = (position size * entry price) / leverage
  const realTimeUsedMargin = useMemo(() => {
    if (positions.length === 0) return 0;
    return positions.reduce((total, position) => {
      return total + (position.size * position.entryPrice) / position.leverage;
    }, 0);
  }, [positions]);
  
  // Calculate real-time available balance
  // Formula: Collateral + Unrealized PnL - Used Margin
  const realTimeAvailableBalance = useMemo(() => {
    if (!summary) return 0;
    const unrealizedPnl = positions.length > 0 ? realTimeUnrealizedPnL : summary.unrealizedPnl;
    // Always use calculated margin (it's 0 when no positions, correct when positions exist)
    return summary.collateral + unrealizedPnl - realTimeUsedMargin;
  }, [summary, positions.length, realTimeUnrealizedPnL, realTimeUsedMargin]);

  // Get real-time market price from Zustand store (WebSocket updates)
  // This ensures the order form uses the same price as the order book display
  const currentPrice = useMarketPrice(market);

  useEffect(() => {
    // Close dropdown when switching between mobile/desktop
    setOrderTypeMenuOpen(false);
  }, [isMobile]);

  const closeOrderMenus = useCallback(() => {
    setOrderTypeMenuOpen(false);
  }, []);

  // Handler for order type selection
  const handleOrderTypeSelect = useCallback(
    (type: "market" | "limit") => {
      setOrderType(type);
      closeOrderMenus();
    },
    [closeOrderMenus]
  );

  const orderTypeLabel = orderType === "market" ? "Market" : "Limit";

  // Calculate position size based on order value
  const positionSize = useMemo(() => {
    const orderVal = parseFloat(orderValue) || 0;
    if (orderVal === 0 || currentPrice === 0) return 0;
    
    if (valueCurrency === "base") {
      // User entered amount in base currency (BTC, SOL, etc.)
      return orderVal;
    } else {
      // User entered amount in USDC, convert to base currency
      return orderVal / currentPrice;
    }
  }, [orderValue, valueCurrency, currentPrice]);

  // Calculate notional value (always in USDC)
  const notionalValue = useMemo(() => {
    return positionSize * currentPrice;
  }, [positionSize, currentPrice]);

  // Calculate slippage estimation for market orders
  const slippageEstimation = useSlippageEstimation({
    orderSize: positionSize,
    market,
    currentPrice,
    vlpQuote: vlpQuote || null,
    userTolerance: slippageTolerance,
    isBuy: true, // Will be determined when placing order
  });

  // Calculate required margin
  const requiredMargin = useMemo(() => {
    return notionalValue / leverage;
  }, [notionalValue, leverage]);

  // Calculate max position value based on leverage tier limits
  // The effective max is the minimum of: (available balance * leverage) and (leverage tier limit)
  const leverageTierMax = useMemo(() => {
    return getMaxPositionFromConfig(leverage, leverageConfig);
  }, [leverage, leverageConfig]);

  const maxPositionValue = useMemo(() => {
    const balanceBasedMax = realTimeAvailableBalance * leverage;
    return Math.min(balanceBasedMax, leverageTierMax);
  }, [realTimeAvailableBalance, leverage, leverageTierMax]);

  // Check if order exceeds max position limit
  const exceedsMaxPosition = useMemo(() => {
    return notionalValue > maxPositionValue && notionalValue > 0;
  }, [notionalValue, maxPositionValue]);

  // Calculate price for preview API
  // Hyperliquid method: Use market price (Binance) for all trading operations
  const priceNumber = useMemo(() => {
    if (orderType === "limit" && limitPrice) {
      const parsed = parseFloat(limitPrice);
      return parsed > 0 ? parsed : currentPrice;
    }
    // Market orders use current market price (Binance)
    // This matches what charts show and what backend uses for liquidations
    return currentPrice;
  }, [orderType, limitPrice, currentPrice]);

  // Backend-calculated liquidation prices (authoritative, includes funding)
  // Industry standard: Backend is source of truth for all risk metrics
  const longPreview = useOrderPreview({
    userId: effectiveUserId ?? null,
    market,
    side: "long",
    size: positionSize,
    price: priceNumber,
    leverage,
    orderType: orderType === "market" ? "market" : "limit",
    enabled: isEffectivelyConnected && positionSize > 0,
  });

  const shortPreview = useOrderPreview({
    userId: effectiveUserId ?? null,
    market,
    side: "short",
    size: positionSize,
    price: priceNumber,
    leverage,
    orderType: orderType === "market" ? "market" : "limit",
    enabled: isEffectivelyConnected && positionSize > 0,
  });

  // Extract liquidation prices from backend previews
  // Backend calculation includes funding payments and all factors
  const estimatedLongLiqPrice = longPreview.preview
    ? parseFloat(longPreview.preview.estimated_liquidation_price)
    : 0;

  const estimatedShortLiqPrice = shortPreview.preview
    ? parseFloat(shortPreview.preview.estimated_liquidation_price)
    : 0;

  // Loading state for liquidation prices
  const isLiqPriceLoading = longPreview.isLoading || shortPreview.isLoading;

  const onOrderValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Remove commas and strip non-numeric characters (allow only digits and decimal point)
      const rawValue = e.target.value.replace(/,/g, "").replace(/[^0-9.]/g, "");
      // Only allow valid decimal format (prevents multiple dots)
      if (rawValue === "" || /^\d*\.?\d*$/.test(rawValue)) {
        setOrderValue(rawValue);
      }
    },
    []
  );

  // Format the display value with thousand separators (only when not focused)
  const displayValue = useMemo(() => {
    if (!orderValue) return "";
    // Show raw value while focused to prevent cursor jumping
    if (isValueFocused) return orderValue;
    const num = parseFloat(orderValue);
    if (isNaN(num)) return orderValue;
    // Format with thousand separators but preserve decimals
    const parts = orderValue.split(".");
    parts[0] = parseInt(parts[0]).toLocaleString("en-US");
    return parts.join(".");
  }, [orderValue, isValueFocused]);

  // Format the limit price with thousand separators (only when not focused)
  const displayLimitPrice = useMemo(() => {
    if (!limitPrice) return "";
    // Show raw value while focused to prevent cursor jumping
    if (isLimitPriceFocused) return limitPrice;
    const num = parseFloat(limitPrice);
    if (isNaN(num)) return limitPrice;
    // Format with thousand separators but preserve decimals
    const parts = limitPrice.split(".");
    parts[0] = parseInt(parts[0]).toLocaleString("en-US");
    return parts.join(".");
  }, [limitPrice, isLimitPriceFocused]);

  const toggleValueCurrency = useCallback(() => {
    setValueCurrency((prev) => (prev === "quote" ? "base" : "quote"));
  }, []);

  const onLimitPriceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Remove commas and validate
      const rawValue = e.target.value.replace(/,/g, "");
      const lastChar = rawValue.slice(-1);
      if (invalidChars.includes(lastChar)) {
        return;
      }
      // Only allow valid decimal format (prevents multiple dots)
      if (rawValue === "" || /^\d*\.?\d*$/.test(rawValue)) {
        setLimitPrice(rawValue);
      }
    },
    []
  );

  const setMidPrice = useCallback(() => {
    if (currentPrice > 0) {
      setLimitPrice(currentPrice.toFixed(quote.precision));
    }
  }, [currentPrice, quote.precision]);

  const handlePlaceOrder = useCallback(
    async (side: "long" | "short") => {
      if (!isEffectivelyConnected || !effectiveUserId) {
        toast.error("Please connect your wallet or enable demo mode");
        return;
      }

      if (!orderValue || parseFloat(orderValue) <= 0) {
        toast.error("Please enter order value");
        return;
      }

      if (requiredMargin > realTimeAvailableBalance) {
        toast.error("Insufficient balance for required margin");
        return;
      }

      // Check if order exceeds leverage tier limit
      if (notionalValue > leverageTierMax) {
        toast.error(`Position exceeds maximum for ${leverage}x leverage`, {
          description: `Max position at ${leverage}x: ${formatCurrency(leverageTierMax, 0)} USD`,
        });
        return;
      }

      // Check minimum order value (industry standard: USD notional minimum)
      // Use 0.5% tolerance to handle floating-point precision (same as display check)
      if (notionalValue < (minOrderValueUsd * MIN_ORDER_TOLERANCE)) {
        toast.error("Order too small", {
          description: `Minimum order value for ${market} is $${minOrderValueUsd}.\nYour order value: $${formatCurrency(notionalValue, 2)}`,
        });
        return;
      }

      if (!currentPrice || currentPrice === 0) {
        toast.error("Market price not available");
        return;
      }

      // Protect user from placing orders with mismatched TP/SL configuration
      // Detect intended side from TP vs SL relationship
      if (takeProfit && stopLoss) {
        const tpNum = parseFloat(takeProfit);
        const slNum = parseFloat(stopLoss);
        const detectedSide = tpNum > slNum ? "long" : "short";
        
        if (side === "long" && detectedSide === "short") {
          toast.error("Your take profit is set below your stop loss", {
            description: "Adjust TP/SL values to place a Long order.",
            duration: 4000,
          });
          return;
        }
        
        if (side === "short" && detectedSide === "long") {
          toast.error("Your take profit is set above your stop loss", {
            description: "Adjust TP/SL values to place a Short order.",
            duration: 4000,
          });
          return;
        }
      }

      // Capture values before clearing input
      const orderSize = positionSize;
      const orderNotionalValue = notionalValue;
      const isMarketOrder = orderType === "market";
      const previousOrderValue = orderValue;

      // ✨ INSTANT FEEDBACK: Clear input immediately
      setOrderValue("");
      setIsSubmitting(true);

      // For market orders: OPTIMISTIC UI - position appears instantly
      // For limit orders: show loading toast (they appear in Open Orders, not Positions)
      const loadingToast = isMarketOrder 
        ? null 
        : toast.loading("Placing limit order...");

      // ✨ OPTIMISTIC POSITION: For market orders, add position to store immediately
      // This makes the position appear in the table BEFORE the API responds
      if (isMarketOrder) {
        try {
          // Check for existing position to handle adding to it
          const existingPosition = useAccountStore.getState().positions.get(market);
          
          // Calculate the unsigned position size
          // If existing position same side: add sizes. Different side: reduce (handled by backend)
          const unsignedSize = existingPosition && existingPosition.side === side
            ? existingPosition.size + orderSize
            : orderSize;
          
          // IMPORTANT: Backend uses SIGNED sizes (negative for short, positive for long)
          // The store conversion (toStorePosition) determines side from: size >= 0 ? "long" : "short"
          // So we must use negative size for short positions to prevent UI flicker
          const signedSize = side === "short" ? -unsignedSize : unsignedSize;
          
          // Create optimistic position in backend format
          const optimisticPosition = {
            user_id: effectiveUserId,
            market,
            size: signedSize.toString(),
            // Use current price as entry estimate - PnL starts at ~$0
            // Real entry price from backend will replace this (slight jump is acceptable)
            entry_price: existingPosition && existingPosition.side === side
              ? existingPosition.entryPrice.toString() // Keep existing confirmed entry
              : currentPrice.toString(), // New position: use current price as estimate
            mark_price: currentPrice.toString(),
            unrealized_pnl: "0", // Start at 0 - will update when backend confirms
            realized_pnl: existingPosition?.realizedPnl?.toString() || "0",
            leverage: leverage.toString(),
            liquidation_price: null, // Backend will calculate
            margin: (orderNotionalValue / leverage).toString(),
            opened_at: existingPosition?.openedAt?.toISOString() || new Date().toISOString(),
            updated_at: new Date().toISOString(),
            take_profit: existingPosition?.takeProfit?.toString() || null,
            stop_loss: existingPosition?.stopLoss?.toString() || null,
          };
          
          // Only add optimistic position for NEW positions or same-side adds
          // Opposite side is a close/reduce - don't show optimistic for those
          const isOpposingSide = existingPosition && existingPosition.side !== side;
          if (!isOpposingSide) {
            useAccountStore.getState().updatePositionFromHttp(optimisticPosition);
          }
        } catch (storeError) {
          console.error("[OrderForm] Failed to add optimistic position:", storeError);
        }
      }

      try {
        // Use limit price if limit order, otherwise use current price
        const executionPrice = orderType === "limit" && limitPrice 
          ? parseFloat(limitPrice) 
          : currentPrice;

        const result = await TradingEngine.placeOrder(effectiveUserId, {
          market,
          side,
          size: orderSize,
          leverage,
          orderType,
          price: executionPrice,
          reduceOnly,
          slippageTolerance: orderType === "market" ? slippageTolerance : undefined,
          timeInForce: orderType === "limit" ? timeInForce : undefined, // Only for limit orders
          takeProfit: takeProfit ? parseFloat(takeProfit) : undefined,
          stopLoss: stopLoss ? parseFloat(stopLoss) : undefined,
          // Store original USD value when user placed order in USD mode
          // This preserves the exact value (e.g., $5,000 instead of $4,828.66 from rounding)
          notionalValue: valueCurrency === "quote" ? parseFloat(previousOrderValue) : undefined,
        });

        // Dismiss loading toast (only exists for limit orders)
        if (loadingToast) toast.dismiss(loadingToast);

        if (result.success) {
          // Apply order to store immediately so it appears in Open Orders
          // This is especially important for limit orders which don't create positions immediately
          if (result.order) {
            try {
              useAccountStore.getState().applyMutationResult({ order: result.order });
            } catch (storeError) {
              console.error("[OrderForm] Failed to add order to store:", storeError);
            }
          }
          
          // Apply REAL position from HTTP response to store
          // This overwrites the optimistic position with accurate data
          if (result.backendPosition) {
            try {
              useAccountStore.getState().updatePositionFromHttp(result.backendPosition);
            } catch (storeError) {
              console.error("[OrderForm] Failed to update position in store:", storeError);
            }
          } else if (isMarketOrder) {
            // No position returned for market order - backend deleted it (fully closed)
            // Remove from store for instant UI feedback
            try {
              useAccountStore.getState().removePosition(market);
            } catch (storeError) {
              console.error("[OrderForm] Failed to remove closed position:", storeError);
            }
          }
          
          // Show success toast - fast animation for market orders
          const successMessage = isMarketOrder 
            ? `${side === "long" ? "Long" : "Short"} position opened`
            : `${side === "long" ? "Long" : "Short"} limit order placed`;
          
          toast.success(successMessage, {
            description: `Size: ${formatCurrency(orderSize, 4)} ${base.code} (~$${formatCurrency(orderNotionalValue, 2)})`,
            className: isMarketOrder ? 'fast-toast' : undefined,
          });
        } else {
          // Order failed - remove optimistic position if we added one
          if (isMarketOrder) {
            try {
              // Check if we need to restore original position or remove entirely
              const existingPosition = useAccountStore.getState().positions.get(market);
              if (existingPosition) {
                // We may have modified an existing position - refetch to restore
                // For now, just let WebSocket restore the correct state
              }
            } catch (storeError) {
              console.error("[OrderForm] Failed to rollback optimistic position:", storeError);
            }
          }
          
          // Restore the input value
          setOrderValue(previousOrderValue);
          
          // Show error with details from backend
          toast.error("Order failed", {
            description: result.error,
          });
        }
      } catch (error) {
        // Dismiss loading toast (only exists for limit orders)
        if (loadingToast) toast.dismiss(loadingToast);
        
        // Order failed - for market orders, WebSocket will restore correct state
        // If error occurred, restore the input value
        setOrderValue(previousOrderValue);
        
        toast.error("Order failed", {
          description: error instanceof Error ? error.message : "Unknown error",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [isEffectivelyConnected, effectiveUserId, orderValue, currentPrice, market, positionSize, leverage, base.code, requiredMargin, realTimeAvailableBalance, reduceOnly, orderType, limitPrice, notionalValue, leverageTierMax, minOrderValueUsd, slippageTolerance, timeInForce]
  );

  const onLong = useCallback(() => handlePlaceOrder("long"), [handlePlaceOrder]);
  const onShort = useCallback(() => handlePlaceOrder("short"), [handlePlaceOrder]);

  // Quick adjust tolerance without opening dialog
  const handleQuickAdjustTolerance = useCallback(() => {
    setSlippageTolerance(slippageEstimation.suggestedTolerance);
    toast.success(`Slippage tolerance increased to ${(slippageEstimation.suggestedTolerance * 100).toFixed(2)}%`);
  }, [setSlippageTolerance, slippageEstimation.suggestedTolerance]);

  const renderMobileOrderTypeControl = () => (
    <div className="relative shrink-0">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOrderTypeMenuOpen((prev) => !prev)}
        className="flex items-center justify-between text-[11px] px-2 py-1 shrink-0"
      >
        <span>{orderTypeLabel}</span>
        <svg
          className={`w-3 h-3 transition-transform ${
            orderTypeMenuOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </Button>
      {orderTypeMenuOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-10 cursor-default"
            aria-label="Close order type menu"
            onClick={() => setOrderTypeMenuOpen(false)}
          />
          <div className="absolute left-0 right-0 z-20 mt-1 rounded-lg border border-border/40 bg-card shadow-lg py-1.5 px-1 min-w-[100px]">
            <button
              type="button"
              onClick={() => handleOrderTypeSelect("market")}
              className={`w-full px-3 py-2 text-xs text-left transition-colors rounded-md ${
                orderType === "market"
                  ? "bg-muted/60 text-foreground"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              Market
            </button>
            <button
              type="button"
              onClick={() => handleOrderTypeSelect("limit")}
              className={`w-full px-3 py-2 text-xs text-left transition-colors rounded-md ${
                orderType === "limit"
                  ? "bg-muted/60 text-foreground"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              Limit
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      <form className={`flex flex-col ${isMobile ? "gap-3.5" : "gap-4"}`}>
        {/* Trading Settings Buttons */}
        {isMobile ? (
          <div className="flex items-center justify-between gap-1 w-full">
            {renderMobileOrderTypeControl()}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => requireAuth(() => setAccountModeDialogOpen(true))}
              className="text-[11px] px-2 py-1 shrink-0 min-w-[3.5rem] text-center"
            >
              {accountMode === "cross" ? "Cross" : "Isolated"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => requireAuth(() => setLeverageDialogOpen(true))}
              className="text-[11px] px-2 py-1 shrink-0 min-w-[3.5rem] text-center"
            >
              {leverage}x
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {/* Order Type Button with Dropdown */}
            <div className="relative">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setOrderTypeMenuOpen(!orderTypeMenuOpen)}
                className="text-xs w-full flex items-center justify-between"
              >
                <span>{orderTypeLabel}</span>
                <svg
                  className={`w-3 h-3 transition-transform ${
                    orderTypeMenuOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </Button>
              {orderTypeMenuOpen && (
                <>
                  <button
                    type="button"
                    className="fixed inset-0 z-10 cursor-default"
                    aria-label="Close order type menu"
                    onClick={() => setOrderTypeMenuOpen(false)}
                  />
                  <div className="absolute left-0 right-0 z-20 mt-1 rounded-lg border border-border/40 bg-card shadow-lg py-1.5 px-1 min-w-[100px]">
                    <button
                      type="button"
                      onClick={() => handleOrderTypeSelect("market")}
                      className={`w-full px-3 py-2 text-xs text-left transition-colors rounded-md ${
                        orderType === "market"
                          ? "bg-muted/60 text-foreground"
                          : "text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      Market
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOrderTypeSelect("limit")}
                      className={`w-full px-3 py-2 text-xs text-left transition-colors rounded-md ${
                        orderType === "limit"
                          ? "bg-muted/60 text-foreground"
                          : "text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      Limit
                    </button>
                  </div>
                </>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => requireAuth(() => setAccountModeDialogOpen(true))}
              className="text-xs"
              data-tour="margin-mode"
            >
              {accountMode === "cross" ? "Cross" : "Isolated"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => requireAuth(() => setLeverageDialogOpen(true))}
              className="text-xs"
              data-tour="leverage-button"
            >
              {leverage}x
            </Button>
          </div>
        )}

        <div className="flex justify-between py-1.5 px-2 bg-muted/50 rounded text-xs" data-tour="available-balance">
          <span className="text-muted-foreground">Available:</span>
          <span className="font-medium">
            ${formatCurrency(realTimeAvailableBalance, 2)}
          </span>
        </div>

        {/* Limit Price Field - Show first when Limit order */}
        {orderType === "limit" && (
          <OrderFormField>
            <div className="relative">
              <OrderFormInput
                prefix="Price"
                suffix=""
                align="start"
                type="text"
                inputMode="decimal"
                value={displayLimitPrice}
                onChange={onLimitPriceChange}
                onFocus={() => setIsLimitPriceFocused(true)}
                onBlur={() => setIsLimitPriceFocused(false)}
                className="pr-28"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {quote.code}
                </span>
                <div className="h-4 w-px bg-border" />
                <button
                  type="button"
                  onClick={setMidPrice}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  title="Set to mid price"
                >
                  Mid
                </button>
              </div>
            </div>
          </OrderFormField>
        )}

        <OrderFormField data-tour="order-input" className={orderType === "limit" ? "-mt-2" : ""}>
          <div className="relative">
            <OrderFormInput
              prefix={valueCurrency === "quote" ? "Value" : "Size"}
              suffix=""
              align="start"
              type="text"
              inputMode="decimal"
              value={displayValue}
              onChange={onOrderValueChange}
              onFocus={() => setIsValueFocused(true)}
              onBlur={() => setIsValueFocused(false)}
              className="pr-20"
            />
            <button
              type="button"
              onClick={toggleValueCurrency}
              className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              title={`Switch to ${valueCurrency === "quote" ? base.code : quote.code}`}
            >
              <ArrowLeftRight className="size-3.5" />
              <span>{valueCurrency === "quote" ? quote.code : base.code}</span>
            </button>
          </div>
        </OrderFormField>

        <ValuePercentageSlider
          availableBalance={realTimeAvailableBalance}
          currentValue={orderValue}
          onValueChange={setOrderValue}
          quotePrecision={quote.precision}
          currentPrice={currentPrice}
          leverage={leverage}
          leverageConfig={leverageConfig}
          valueCurrency={valueCurrency}
          orderType={orderType}
          limitPrice={limitPrice}
          timeInForce={timeInForce}
          onTimeInForceChange={setTimeInForce}
          reduceOnly={reduceOnly}
          onReduceOnlyChange={setReduceOnly}
          onTpSlChange={(tp, sl) => {
            setTakeProfit(tp);
            setStopLoss(sl);
          }}
        />

        {/* Slippage Warning - Only show when tolerance exceeded */}
        {orderType === "market" && orderValue && parseFloat(orderValue) > 0 && (
          <SlippageWarning
            estimation={slippageEstimation}
            onAcceptAndContinue={handleQuickAdjustTolerance}
          />
        )}

      {/* Trade Execution Area - Liquidation Prices + Buy/Sell Buttons */}
      <div data-tour="trade-execution-area" className="flex flex-col gap-2">
        {/* Liquidation Prices (Long/Short) - Above buttons */}
        {!isMobile && (
          <LiquidationPriceDisplay
            longLiqPrice={estimatedLongLiqPrice}
            shortLiqPrice={estimatedShortLiqPrice}
            quotePrecision={quote.precision}
            isLoading={isLiqPriceLoading}
          />
        )}

        {/* Buy/Sell Buttons - Always shown */}
        <div className={`${isMobile ? "flex flex-col" : "grid grid-cols-2"} gap-2`}>
          <Button
            variant="buy"
            size="lg"
            onClick={() => {
              if (!isEffectivelyConnected) {
                setLoginModalOpen(true);
                return;
              }
              onLong();
            }}
            type="button"
            disabled={isSubmitting || (isEffectivelyConnected && (!orderValue || parseFloat(orderValue) <= 0 || exceedsMaxPosition || (orderType === "market" && slippageTolerance !== null && slippageEstimation.exceedsTolerance)))}
            className="w-full disabled:opacity-100"
          >
            Buy / Long
          </Button>
          <Button
            variant="sell"
            size="lg"
            onClick={() => {
              if (!isEffectivelyConnected) {
                setLoginModalOpen(true);
                return;
              }
              onShort();
            }}
            type="button"
            disabled={isSubmitting || (isEffectivelyConnected && (!orderValue || parseFloat(orderValue) <= 0 || exceedsMaxPosition || (orderType === "market" && slippageTolerance !== null && slippageEstimation.exceedsTolerance)))}
            className="w-full disabled:opacity-100"
          >
            Sell / Short
          </Button>
        </div>
      </div>

      {/* Trading Information Section */}
      <div className="flex flex-col gap-0.5 text-xs">
        <div className="flex justify-between">
          <span className="text-muted-foreground text-[10px]">Margin:</span>
          <span className="font-medium text-[10px]">
            {formatCurrency(requiredMargin, quote.precision)} {quote.code}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground text-[10px]">Max Position:</span>
          <span className={`font-medium text-[10px] ${exceedsMaxPosition ? "text-destructive" : ""}`}>
            {formatCurrency(maxPositionValue, quote.precision)} {quote.code}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground text-[10px]">Fee Rate:</span>
          <span className="font-medium text-[10px]">
            {feesConfig 
              ? `Taker ${feesConfig.taker_fee_percentage}% / Maker ${feesConfig.maker_fee_percentage}%`
              : "Loading..."}
          </span>
        </div>
      </div>
    </form>

    {/* Login Modal - Available globally */}
    <LoginModal open={loginModalOpen} onOpenChange={setLoginModalOpen} />

    {/* Trading Settings Dialogs */}
    <AccountModeDialog
      open={accountModeDialogOpen}
      onOpenChange={setAccountModeDialogOpen}
    />
    <LeverageDialog
      open={leverageDialogOpen}
      onOpenChange={setLeverageDialogOpen}
    />
    <SlippageToleranceDialog
      open={slippageDialogOpen}
      onOpenChange={setSlippageDialogOpen}
      currentOrderSize={positionSize}
      currentPrice={currentPrice}
      suggestedTolerance={slippageEstimation.suggestedTolerance}
    />
  </>
  );
};

